import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { cn } from '@shared/utils/utils';
import { useTranslation } from 'react-i18next';
import type {
  ProfitabilitySummaryAverages,
  ProfitabilitySummaryDailyResponse,
  ProfitabilitySummaryTotals,
} from '../../models/Profitability';
import { formatProfitabilityAmount, formatProfitabilityPercent } from '../../utils/formatMoney';

interface ProfitabilitySummaryDailyTableProps {
  data?: ProfitabilitySummaryDailyResponse;
  isLoading?: boolean;
  locale: string;
}

/** Fecha API `YYYY-MM-DD` → texto local sin desfase por zona horaria */
function formatDailyDate(isoDate: string, locale: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!m) return isoDate;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return isoDate;
  return new Date(y, mo - 1, d).toLocaleDateString(locale, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function AmountCell({
  value,
  locale,
  currency,
  signed,
}: {
  value: number | null | undefined;
  locale: string;
  currency: string;
  signed?: boolean;
}) {
  const isNegative = signed && value != null && value < 0;
  return (
    <TableCell className={cn('text-right tabular-nums text-sm', isNegative ? 'text-destructive' : 'text-foreground')}>
      {formatProfitabilityAmount(value, locale, currency)}
    </TableCell>
  );
}

function DailyTotalsRow({
  label,
  ordersCount,
  totals,
  averages,
  locale,
  currency,
}: {
  label: string;
  ordersCount: number;
  totals: ProfitabilitySummaryTotals;
  averages: ProfitabilitySummaryAverages;
  locale: string;
  currency: string;
}) {
  return (
    <TableRow className="border-border bg-muted/40 font-medium hover:bg-muted/40">
      <TableCell className="sticky left-0 z-10 bg-muted/40 text-sm text-foreground">{label}</TableCell>
      <TableCell className="text-right tabular-nums text-sm">{ordersCount.toLocaleString(locale)}</TableCell>
      <AmountCell value={totals.recognized_revenue} locale={locale} currency={currency} />
      <AmountCell value={totals.recognized_cogs} locale={locale} currency={currency} />
      <AmountCell value={totals.recognized_logistics} locale={locale} currency={currency} />
      <AmountCell value={totals.operating_profit} locale={locale} currency={currency} signed />
      <AmountCell value={totals.ad_spend} locale={locale} currency={currency} />
      <AmountCell value={totals.profit} locale={locale} currency={currency} signed />
      <TableCell className="text-right tabular-nums text-sm">
        {formatProfitabilityAmount(averages.profit_per_order, locale, currency)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        {formatProfitabilityPercent(averages.margin_percent, locale)}
      </TableCell>
    </TableRow>
  );
}

export const ProfitabilitySummaryDailyTable = ({ data, isLoading, locale }: ProfitabilitySummaryDailyTableProps) => {
  const { t } = useTranslation('profitabilityModule');
  const currency = useCurrency();
  const days = data?.days ?? [];
  const colCount = 10;

  return (
    <div data-testid="profitability-summary-daily-table">
      <div className="mb-4 flex items-center gap-2">
        <h2 className="text-lg font-semibold text-title">{t('summary.daily.title')}</h2>
        <InfoTooltip
          text={t('summary.daily.hint')}
          dataTestId="profitability-summary-daily-info"
          iconClassName="w-4 h-4"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="sticky left-0 z-10 min-w-[140px] bg-background text-muted-foreground">
                {t('summary.daily.col_date')}
              </TableHead>
              <TableHead className="text-right text-muted-foreground">{t('summary.daily.col_orders')}</TableHead>
              <TableHead className="text-right text-muted-foreground">{t('summary.recognized_revenue')}</TableHead>
              <TableHead className="text-right text-muted-foreground">{t('summary.recognized_cogs')}</TableHead>
              <TableHead className="text-right text-muted-foreground">{t('summary.recognized_logistics')}</TableHead>
              <TableHead className="text-right text-muted-foreground">
                {t('summary.daily.col_operating_profit')}
              </TableHead>
              <TableHead className="text-right text-muted-foreground">{t('by_product.row.ad_spend')}</TableHead>
              <TableHead className="text-right text-muted-foreground">{t('summary.profit')}</TableHead>
              <TableHead className="text-right text-muted-foreground">{t('summary.profit_per_order')}</TableHead>
              <TableHead className="text-right text-muted-foreground">{t('summary.margin_percent')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={colCount} className="py-10 text-center text-muted-foreground">
                  …
                </TableCell>
              </TableRow>
            )}

            {!isLoading && days.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} className="py-10 text-center text-sm text-muted-foreground">
                  {t('summary.daily.empty')}
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              days.map((day) => (
                <TableRow
                  key={day.date}
                  className="border-border"
                  data-testid={`profitability-summary-daily-row-${day.date}`}
                >
                  <TableCell className="sticky left-0 z-10 bg-background text-sm text-foreground">
                    {formatDailyDate(day.date, locale)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {day.orders_count.toLocaleString(locale)}
                  </TableCell>
                  <AmountCell value={day.totals.recognized_revenue} locale={locale} currency={currency} />
                  <AmountCell value={day.totals.recognized_cogs} locale={locale} currency={currency} />
                  <AmountCell value={day.totals.recognized_logistics} locale={locale} currency={currency} />
                  <AmountCell value={day.totals.operating_profit} locale={locale} currency={currency} signed />
                  <AmountCell value={day.totals.ad_spend} locale={locale} currency={currency} />
                  <AmountCell value={day.totals.profit} locale={locale} currency={currency} signed />
                  <TableCell className="text-right tabular-nums text-sm">
                    {formatProfitabilityAmount(day.averages.profit_per_order, locale, currency)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {formatProfitabilityPercent(day.averages.margin_percent, locale)}
                  </TableCell>
                </TableRow>
              ))}

            {!isLoading && data && days.length > 0 && (
              <DailyTotalsRow
                label={t('summary.daily.total_row')}
                ordersCount={data.orders_count}
                totals={data.totals}
                averages={data.averages}
                locale={locale}
                currency={currency}
              />
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
